import React from 'react'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Table from 'components/Table'
import HehmInvoiceReport from 'types/HehmInvoiceReport'
import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

export type PaymentOverride = {
  amount: string
  refId: string
}

type Props = {
  invoice: HehmInvoiceReport
  isEven: boolean
  override: PaymentOverride
  onOverrideChange: (field: 'amount' | 'refId', value: string) => void
  onPayPress: () => void
  showSpinner?: boolean
}

const InvoiceTableRow = ({ invoice, isEven, override, onOverrideChange, onPayPress, showSpinner }: Props) => (
  <S.Row $isEven={isEven}>
    <S.Cell>{invoice.dealerId}</S.Cell>
    <S.Cell>{invoice.dealerName}</S.Cell>
    <S.Cell>{invoice.organizationName}</S.Cell>
    <S.Cell>{invoice.location}</S.Cell>
    <S.Cell>{invoice.r12AccountNumber}</S.Cell>
    <S.Cell>{invoice.salesperson}</S.Cell>
    <S.Cell>{invoice.defaultBranch}</S.Cell>
    <S.ChargesAndTaxesCell>
      {invoice.charges.map((charge) => (
        <S.ChargeItem key={`${invoice.invoiceNo}-${charge.description}`}>
          <S.ChargeDescription>{charge.description}</S.ChargeDescription>
          <S.ChargeAmount>${Number(charge.amount).toFixed(2)}</S.ChargeAmount>
          <S.ChargeDetails>{charge.units} × {charge.serviceName}</S.ChargeDetails>
        </S.ChargeItem>
      ))}
    </S.ChargesAndTaxesCell>
    <S.ChargesAndTaxesCell>
      {invoice.taxes.map((tax) => (
        <S.ChargeItem key={`${invoice.invoiceNo}-${tax.description}`}>
          <S.ChargeDescription>{tax.description}</S.ChargeDescription>
          <S.ChargeAmount>${Number(tax.amount).toFixed(2)}</S.ChargeAmount>
          <S.ChargeDetails>{tax.jurisdiction} @ {(tax.taxRate * 100).toFixed(1)}%</S.ChargeDetails>
        </S.ChargeItem>
      ))}
    </S.ChargesAndTaxesCell>
    <S.TotalCell>${Number(invoice.totalAmount).toFixed(2)}</S.TotalCell>
    <S.Cell>{invoice.glCode}</S.Cell>
    <S.Cell>{invoice.productSku}</S.Cell>
    <S.Cell>
      <S.Input
        type="text"
        placeholder="Amount"
        value={override.amount}
        onChange={(e) => onOverrideChange('amount', e.target.value)}
      />
    </S.Cell>
    <S.Cell>
      <S.Input
        type="text"
        placeholder="Ref ID"
        value={override.refId}
        onChange={(e) => onOverrideChange('refId', e.target.value)}
      />
    </S.Cell>
    <S.Cell>
      <Button
        decoration="primary"
        text="Pay"
        onPress={onPayPress}
        showSpinner={showSpinner}
      />
    </S.Cell>
  </S.Row>
)

export default InvoiceTableRow

const S = {
  Row: styled(Table.Row)<{ $isEven?: boolean }>`
    background-color: ${({ $isEven }) => $isEven ? color('rowBackground') : color('paneBackground')};
    border-bottom: 1px solid ${color('rowBorder')};
    align-items: stretch;
    min-height: 60px;
    min-width: max-content;

    &:hover {
      background-color: ${color('rowHoverBackground')};
    }

    @media ${screenSize.small} {
      flex-direction: column;
      padding: 12px;
    }
  `,
  Cell: styled(Table.Cell)`
    padding: 10px 8px;
    font-size: 13px;
    align-items: center;
    min-width: 80px;
    word-break: break-word;
    color: ${color('primaryText')};
  `,
  TotalCell: styled(Table.Cell)`
    padding: 10px 8px;
    font-size: 13px;
    font-weight: 600;
    align-items: center;
    min-width: 80px;
    color: ${color('primaryText')};
  `,
  ChargesAndTaxesCell: styled(Table.Cell)`
    flex-direction: column;
    gap: 8px;
    padding: 10px 8px;
    min-width: 160px;
    flex: 2;
    align-items: flex-start;
    justify-content: center;
  `,
  ChargeItem: styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px 8px;
    background-color: ${color('chargeItemBackground')};
    border-radius: 4px;
    width: 100%;
  `,
  ChargeDescription: styled.span`
    font-size: 12px;
    font-weight: 500;
    color: ${color('primaryText')};
  `,
  ChargeAmount: styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${color('primaryText')};
  `,
  ChargeDetails: styled.span`
    font-size: 11px;
    color: ${color('secondaryText')};
  `,
  Input: styled.input`
    padding: 6px 8px;
    border: 1px solid ${color('border')};
    border-radius: 4px;
    font-size: 13px;
    width: 80px;

    &:focus {
      outline: none;
      border-color: ${color('borderGray')};
      box-shadow: 0 0 0 2px ${color('inputFocusRing')};
    }

    &::placeholder {
      color: ${color('secondaryText')};
      font-size: 11px;
    }
  `
}
